import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { SeasonalityCard } from "@/components/dashboard/SeasonalityCard";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import { getGoldSeasonality, type MonthStats } from "@/lib/seasonality.functions";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-seasonality";
const TITLE = "Gold Seasonality: XAUUSD Performance by Month | XAU Pro Academy";
const DESC =
  "Free gold seasonality tool. XAUUSD average monthly returns over 1, 5 and 10 years, win rate by month, best and worst years, and the current month to date.";

const FAQ = [
  { q: "What is gold seasonality?", a: "Gold seasonality measures how XAUUSD has historically performed in each calendar month. It shows average returns and how often a month closed higher, based on monthly open and close prices." },
  { q: "Is October historically bullish for gold?", a: "Select October in the tool above to see the latest 10 year average, the share of positive years and the best and worst outcomes. Readings update automatically as each month closes." },
  { q: "What is the best month to trade gold?", a: "No month is reliably bullish or bearish. Seasonality describes historical tendency, not a forecast. It works best as background context next to market structure, liquidity and macro events." },
  { q: "Can I trade seasonality alone?", a: "No. A seasonal tendency never defines an entry, stop or target. Execution still requires structure, displacement, liquidity interaction and defined invalidation." },
  { q: "Where does the data come from?", a: "Monthly XAUUSD spot open and close prices from our market data provider, stored in our monthly history and refreshed after each daily close. These are spot market figures, not strategy results." },
];

export const Route = createFileRoute("/gold-seasonality")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "gold seasonality, gold seasonality chart, XAUUSD seasonal patterns, gold performance by month, best month for gold, gold monthly returns" },
      { property: "og:title", content: "Gold Seasonality Chart: XAUUSD Performance by Month" },
      { property: "og:description", content: "Free 10 year gold seasonality by month with win rates, averages and month to date." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "Dataset", name: "Gold (XAUUSD) Monthly Seasonality", description: DESC, url: `${SITE}${PATH}`, license: "https://xauproacademy.com/terms", isAccessibleForFree: true, inLanguage: "en", variableMeasured: ["Average monthly XAUUSD return", "Positive month ratio", "Median monthly return"], distribution: [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: `${SITE}${PATH}` }], dateModified: new Date().toISOString().slice(0, 10), creator: { "@type": "Organization", name: "XAU Pro Academy", url: SITE } }) },
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }) },
      { type: "application/ld+json", children: JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [ { "@type": "ListItem", position: 1, name: "Home", item: SITE }, { "@type": "ListItem", position: 2, name: "Gold Seasonality", item: `${SITE}${PATH}` } ] }) },
    ],
  }),
  loader: () => getGoldSeasonality(),
  staleTime: 60 * 60 * 1000,
  component: SeasonalityPage,
});

const fmt = (v: number | null) => (v == null ? "n/a" : `${v > 0 ? "+" : ""}${v.toFixed(2)}%`);
const tone = (v: number | null) => (v == null ? "var(--muted-foreground)" : v > 0 ? "var(--bias-bull)" : v < 0 ? "var(--bias-bear)" : "var(--muted-foreground)");

function SeasonalityPage() {
  const data = Route.useLoaderData();
  const months: MonthStats[] = data?.months ?? [];
  const maxAbs = Math.max(1, ...months.map((m) => Math.abs(m.avg10y ?? 0)));

  return (
    <Layout>
      <section className="container mx-auto max-w-5xl px-4 pt-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Free gold market tool</div>
        <h1 className="mt-3 font-display text-3xl font-bold md:text-5xl">Gold Seasonality: XAUUSD Performance by Month</h1>
        <p className="mt-4 max-w-3xl text-muted-foreground">
          How gold has behaved in each calendar month over the last 10 years. Averages, positive year share, best and worst
          outcomes, and the current month to date. Historical tendency is context, not a forecast.
        </p>
      </section>

      <section className="container mx-auto max-w-5xl px-4 pt-10">
        <SeasonalityCard />
      </section>

      <section className="container mx-auto max-w-5xl px-4 pt-10">
        <h2 className="font-display text-2xl font-semibold">10 year monthly heatmap</h2>
        <p className="mt-2 text-sm text-muted-foreground">Average return and share of positive years for every month.</p>
        <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <tr className="border-b border-border">
                <th className="p-3 text-left">Month</th><th className="p-3 text-right">10Y avg</th><th className="p-3 text-right">5Y avg</th>
                <th className="p-3 text-right">Positive</th><th className="p-3 text-right">Best</th><th className="p-3 text-right">Worst</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {months.map((m) => (
                <tr key={m.month} className="border-b border-border/50 last:border-0">
                  <td className="p-3 font-sans">{m.monthName}{m.month === data?.currentMonth && <span className="ml-2 text-[10px] text-gold">CURRENT</span>}</td>
                  <td className="p-3 text-right">
                    <span className="inline-block rounded px-2 py-0.5" style={{ color: tone(m.avg10y), background: `color-mix(in oklab, ${tone(m.avg10y)} ${Math.round((Math.abs(m.avg10y ?? 0) / maxAbs) * 25)}%, transparent)` }}>{fmt(m.avg10y)}</span>
                  </td>
                  <td className="p-3 text-right" style={{ color: tone(m.avg5y) }}>{fmt(m.avg5y)}</td>
                  <td className="p-3 text-right">{m.pos10y == null ? "n/a" : `${m.pos10y.toFixed(0)}%`}</td>
                  <td className="p-3 text-right" style={{ color: tone(m.best) }}>{fmt(m.best)}</td>
                  <td className="p-3 text-right" style={{ color: tone(m.worst) }}>{fmt(m.worst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 pt-12">
        <h2 className="font-display text-2xl font-semibold">How to use seasonality on gold</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {[
            ["Context, not entry", "A constructive month only shifts the background. Entries still require structure, displacement and defined invalidation."],
            ["Check the sample", "A strong average can hide one outlier year. Compare the average with the share of positive years and the worst outcome."],
            ["Respect macro", "CPI, FOMC and positioning can override any seasonal tendency. Read seasonality next to the COT and CPI tools."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-xl border border-border bg-card p-5">
              <div className="font-display font-semibold">{t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Learn the execution framework in the <Link to="/smart-money-concepts" className="text-gold hover:underline">Smart Money Concepts guide</Link> or the <Link to="/education" className="text-gold hover:underline">Education Library</Link>.
        </p>
      </section>

      <MacroToolsStrip />

      <section className="container mx-auto max-w-5xl px-4 py-12">
        <h2 className="font-display text-2xl font-semibold">Frequently asked questions</h2>
        <div className="mt-4 space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="rounded-xl border border-border bg-card p-4">
              <summary className="cursor-pointer font-medium">{f.q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
        <p className="mt-6 text-xs text-muted-foreground">XAUUSD spot data. Historical tendencies do not guarantee future results and are not strategy performance.</p>
      </section>
    </Layout>
  );
}
